import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * ThemeContext - gestisce 3 modalita':
 * - 'light'  -> forza tema chiaro
 * - 'dark'   -> forza tema scuro
 * - 'auto'   -> alterna in base all'orario (dark tra 18:00 e 07:00, altrimenti light)
 *
 * La modalita' scelta viene persistita in localStorage ('crm-theme-mode').
 * In modalita' auto, un timer controlla ogni minuto se e' cambiata la fascia oraria.
 */
const ThemeContext = createContext(null);

const STORAGE_KEY = 'crm-theme-mode';
const DARK_HOUR_START = 18; // 18:00 -> dark
const DARK_HOUR_END = 7;    // < 07:00 -> dark

const isNightHour = () => {
  const h = new Date().getHours();
  return h >= DARK_HOUR_START || h < DARK_HOUR_END;
};

export const resolveEffectiveTheme = (mode) => {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  return isNightHour() ? 'dark' : 'light';
};

const applyThemeToDocument = (effective) => {
  const root = document.documentElement;
  if (effective === 'dark') {
    root.classList.add('dark');
    root.style.colorScheme = 'dark';
  } else {
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
    } catch (_) { /* ignore */ }
    return 'auto';
  });
  const [effective, setEffective] = useState(() => resolveEffectiveTheme(mode));

  const setMode = useCallback((newMode) => {
    setModeState(newMode);
    try { localStorage.setItem(STORAGE_KEY, newMode); } catch (_) { /* ignore */ }
  }, []);

  // Apply effective theme whenever mode or auto-tick changes it
  useEffect(() => {
    const eff = resolveEffectiveTheme(mode);
    setEffective(eff);
    applyThemeToDocument(eff);
  }, [mode]);

  // In auto mode, re-check every minute so dark kicks in at 18:00 automatically
  useEffect(() => {
    if (mode !== 'auto') return undefined;
    const tick = () => {
      const eff = resolveEffectiveTheme('auto');
      setEffective(prev => {
        if (prev !== eff) applyThemeToDocument(eff);
        return eff;
      });
    };
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, effective, setMode, isNight: effective === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
};
